import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Users, Plus, Trash2, Eye, EyeOff, KeyRound, RefreshCw, Shield, Database, UserPlus,
  Copy, Check, ChevronDown, ChevronUp, MessageSquare, Mail, MailOpen, Clock,
  UtensilsCrossed, Wine, Carrot, Building2, Euro, Truck, Package, X, Link2, AlertCircle, History
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AuditTrail from "../components/admin/AuditTrail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  speisen: { label: "Speisen", icon: UtensilsCrossed },
  getraenke: { label: "Getränke", icon: Wine },
  zutaten: { label: "Zutaten", icon: Carrot },
  mitarbeiter: { label: "Mitarbeiter", icon: Users },
  fixkosten: { label: "Fixkosten", icon: Building2 },
  einnahmen: { label: "Einnahmen", icon: Euro },
  lieferanten: { label: "Lieferanten", icon: Truck },
  einkauf: { label: "Einkauf", icon: Package },
};

const CATEGORY_FIELDS = {
  speisen: ["name", "kategorie", "verkaufspreis", "wareneinsatz_prozent"],
  getraenke: ["name", "kategorie", "verkaufspreis", "wareneinsatz_prozent"],
  zutaten: ["name", "einheit", "preis_pro_einheit", "lieferant"],
  mitarbeiter: ["name", "position", "anstellungsart", "stundenlohn"],
  fixkosten: ["bezeichnung", "betrag", "intervall", "kategorie"],
  einnahmen: ["datum", "betrag", "kategorie"],
  lieferanten: ["name", "kategorie", "telefon", "liefergebuehren"],
  einkauf: ["zutat_name", "lieferant", "menge", "einkaufspreis", "lieferdatum"],
};

const formatValue = (key, val) => {
  if (val == null || val === "") return "-";
  if (["verkaufspreis", "betrag", "stundenlohn", "einkaufspreis", "preis_pro_einheit", "liefergebuehren", "wareneinsatz_prozent"].includes(key)) {
    return typeof val === "number" ? `${val.toFixed(2)} €` : val;
  }
  return String(val);
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [nachrichten, setNachrichten] = useState({ nachrichten: [], ungelesen: 0, gesamt: 0 });
  const [resetRequests, setResetRequests] = useState({ requests: [], count: 0 });
  const [approvedResetLink, setApprovedResetLink] = useState(null);

  const [newUser, setNewUser] = useState({ username: "", password: "", name: "", email: "" });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNachrichten = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/nachrichten`, { withCredentials: true });
      setNachrichten(res.data);
    } catch (err) { 
      console.error('Fehler beim Laden der Nachrichten:', err.message);
    }
  }, []);

  const fetchResetRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/password-reset-requests`, { withCredentials: true });
      setResetRequests(res.data);
    } catch (err) { 
      console.error('Fehler beim Laden der Reset-Anfragen:', err.message);
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchNachrichten(); fetchResetRequests(); }, [fetchUsers, fetchNachrichten, fetchResetRequests]);

  if (user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const handleCreateUser = async () => {
    if (!newUser.username.trim() || !newUser.password) {
      toast.error("Benutzername und Passwort erforderlich");
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, {
        username: newUser.username.trim(),
        password: newUser.password,
        name: newUser.name || newUser.username,
        email: newUser.email || null
      }, { withCredentials: true });
      toast.success(`Benutzer "${newUser.username}" erstellt`);
      setNewUser({ username: "", password: "", name: "", email: "" });
      setShowCreateDialog(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Benutzer "${username}" wirklich löschen? Alle Daten gehen verloren.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(`Benutzer "${username}" gelöscht`);
      if (expandedUser === userId) { setExpandedUser(null); setOverviewData(null); }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error("Passwort muss mind. 6 Zeichen haben");
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${resetTarget.user_id}/password`, {
        password: resetPassword
      }, { withCredentials: true });
      toast.success(`Passwort für "${resetTarget.username}" zurückgesetzt`);
      setShowResetDialog(false);
      setResetPassword("");
      setResetTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Zurücksetzen");
    }
  };

  const loadOverview = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setSelectedCategory(null);
      setCategoryData(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/overview`, { withCredentials: true });
      setOverviewData(res.data);
      setExpandedUser(userId);
      setSelectedCategory(null);
      setCategoryData(null);
    } catch (err) {
      toast.error("Fehler beim Laden der Übersicht");
    }
  };

  const loadCategoryData = async (userId, category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
      setCategoryData(null);
      return;
    }
    setCategoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/data/${category}`, { withCredentials: true });
      setCategoryData(res.data);
      setSelectedCategory(category);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setCategoryLoading(false);
    }
  };

  const markGelesen = async (id) => {
    await axios.put(`${API}/admin/nachrichten/${id}/gelesen`, {}, { withCredentials: true });
    fetchNachrichten();
  };

  const deleteNachricht = async (id) => {
    await axios.delete(`${API}/admin/nachrichten/${id}`, { withCredentials: true });
    toast.success("Nachricht gelöscht");
    fetchNachrichten();
  };

  const approveResetRequest = async (requestId) => {
    try {
      const res = await axios.post(`${API}/admin/password-reset-requests/${requestId}/approve`, {}, { withCredentials: true });
      const fullLink = `${window.location.origin}${res.data.reset_link}`;
      setApprovedResetLink({
        link: fullLink,
        username: res.data.username,
        expiresAt: res.data.expires_at
      });
      toast.success("Reset-Link generiert!");
      fetchResetRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Genehmigen");
    }
  };

  const deleteResetRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/admin/password-reset-requests/${requestId}`, { withCredentials: true });
      toast.success("Anfrage gelöscht");
      fetchResetRequests();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let pw = "";
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    return pw;
  };

  const regularUsers = users.filter(u => u.role !== "admin" && u.user_id !== "demo-cocktailbar-user");

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Shield size={28} className="text-blue-500" />
            Admin Dashboard
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Beta-Test Zugangssteuerung</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="create-user-btn">
          <UserPlus size={18} /> Neuen Zugang erstellen
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Beta-Tester</p>
          <p className="text-3xl font-mono font-bold mt-1" data-testid="user-count">{regularUsers.length}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Gesamt Benutzer</p>
          <p className="text-3xl font-mono font-bold mt-1">{users.length}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Rollen</p>
          <p className="text-3xl font-mono font-bold mt-1">
            <span className="text-blue-500">{users.filter(u => u.role === 'admin').length}</span>
            <span className="text-slate-300 dark:text-slate-600 mx-1">/</span>
            <span className="text-emerald-500">{regularUsers.length}</span>
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">Admin / Tester</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Nachrichten</p>
          <p className="text-3xl font-mono font-bold mt-1">
            {nachrichten.ungelesen > 0 ? (
              <span className="text-amber-500">{nachrichten.ungelesen}</span>
            ) : (
              <span className="text-slate-300 dark:text-slate-600">0</span>
            )}
            <span className="text-slate-300 dark:text-slate-600 mx-1">/</span>
            <span>{nachrichten.gesamt}</span>
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">Ungelesen / Gesamt</p>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">PW-Anfragen</p>
          <p className="text-3xl font-mono font-bold mt-1">
            {resetRequests.count > 0 ? (
              <span className="text-red-500">{resetRequests.count}</span>
            ) : (
              <span className="text-slate-300 dark:text-slate-600">0</span>
            )}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">Ausstehend</p>
        </div>
      </div>

      {/* Tabs: Benutzer + Nachrichten */}
      <Tabs defaultValue="benutzer" className="w-full">
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 mb-4">
          <TabsTrigger value="benutzer" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 gap-2">
            <Users size={16} /> Benutzer ({users.length})
          </TabsTrigger>
          <TabsTrigger value="nachrichten" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 gap-2" data-testid="nachrichten-tab">
            <MessageSquare size={16} /> Nachrichten
            {nachrichten.ungelesen > 0 && (
              <span className="ml-1 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{nachrichten.ungelesen}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="reset-anfragen" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 gap-2" data-testid="reset-requests-tab">
            <KeyRound size={16} /> Passwort-Anfragen
            {resetRequests.count > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{resetRequests.count}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 gap-2" data-testid="audit-trail-tab-btn">
            <History size={16} /> Aktivitaeten
          </TabsTrigger>
        </TabsList>

        {/* BENUTZER TAB */}
        <TabsContent value="benutzer">
          {loading ? (
            <div className="text-center py-10 text-slate-400">Laden...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-10">
              <Users size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Noch keine Benutzer vorhanden</p>
            </div>
          ) : (
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.user_id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden" data-testid={`user-card-${u.username}`}>
                  {/* User Row */}
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        u.role === 'admin' ? 'bg-blue-500' : u.user_id === 'demo-cocktailbar-user' ? 'bg-violet-500' : 'bg-emerald-500'
                      }`}>
                        {u.name?.[0]?.toUpperCase() || u.username?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-semibold text-sm flex items-center gap-2">
                          {u.name || u.username}
                          {u.role === 'admin' && <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded-full uppercase">Admin</span>}
                          {u.user_id === 'demo-cocktailbar-user' && <span className="text-[10px] bg-violet-500 text-white px-1.5 py-0.5 rounded-full uppercase">Demo</span>}
                        </p>
                        <p className="text-xs text-slate-400">@{u.username} {u.email ? `· ${u.email}` : ''} · {u.created_at ? new Date(u.created_at).toLocaleDateString('de-DE') : '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {u.role !== 'admin' && u.user_id !== 'demo-cocktailbar-user' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => loadOverview(u.user_id)} className="text-slate-500 hover:text-blue-600" data-testid={`view-user-${u.username}`} title="Daten einsehen">
                            {expandedUser === u.user_id ? <ChevronUp size={16} /> : <Database size={16} />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setResetTarget(u); setShowResetDialog(true); }} className="text-slate-500 hover:text-amber-600" title="Passwort zurücksetzen">
                            <KeyRound size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.user_id, u.username)} className="text-slate-500 hover:text-red-600" data-testid={`delete-user-${u.username}`} title="Löschen">
                            <Trash2 size={16} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expanded: Data Overview + Category Detail */}
                  {expandedUser === u.user_id && overviewData && (
                    <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-4 space-y-4">
                      <h4 className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-2">
                        <Database size={14} /> Daten von {overviewData.user.name || overviewData.user.username}
                        <span className="font-mono text-slate-400 ml-auto">{overviewData.total_entries} Einträge gesamt</span>
                      </h4>

                      {/* Category Grid - klickbar! */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(overviewData.data_counts).map(([key, count]) => {
                          const cat = CATEGORY_LABELS[key];
                          const Icon = cat?.icon || Database;
                          const isActive = selectedCategory === key;
                          return (
                            <button
                              key={key}
                              onClick={() => count > 0 && loadCategoryData(u.user_id, key)}
                              disabled={count === 0}
                              className={`text-left rounded-lg p-3 border transition-all ${
                                isActive
                                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 ring-1 ring-blue-400'
                                  : count > 0
                                    ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer'
                                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700/50 opacity-50 cursor-default'
                              }`}
                              data-testid={`category-${key}`}
                            >
                              <div className="flex items-center gap-2">
                                <Icon size={14} className={isActive ? 'text-blue-500' : 'text-slate-400'} />
                                <span className="text-xs text-slate-500 dark:text-slate-400">{cat?.label || key}</span>
                              </div>
                              <p className={`text-lg font-mono font-bold mt-1 ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}`}>{count}</p>
                            </button>
                          );
                        })}
                      </div>

                      {/* Category Detail Table */}
                      {categoryLoading && <div className="text-center py-4 text-sm text-slate-400">Lade Daten...</div>}
                      {selectedCategory && categoryData && !categoryLoading && (
                        <div className="mt-3" data-testid="category-detail-table">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-sm font-semibold flex items-center gap-2">
                              {(() => { const Icon = CATEGORY_LABELS[selectedCategory]?.icon || Database; return <Icon size={16} className="text-blue-500" />; })()}
                              {CATEGORY_LABELS[selectedCategory]?.label} ({categoryData.count})
                            </h5>
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedCategory(null); setCategoryData(null); }}>
                              <X size={14} />
                            </Button>
                          </div>
                          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-slate-100 dark:bg-slate-800">
                                  {(CATEGORY_FIELDS[selectedCategory] || []).map(field => (
                                    <th key={field} className="text-left px-3 py-2 text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                                      {field.replace(/_/g, ' ')}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {categoryData.items.slice(0, 30).map((item, idx) => (
                                  <tr key={idx} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                    {(CATEGORY_FIELDS[selectedCategory] || []).map(field => (
                                      <td key={field} className="px-3 py-2 font-mono text-xs">{formatValue(field, item[field])}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {categoryData.items.length > 30 && (
                              <p className="text-center text-xs text-slate-400 py-2">+ {categoryData.items.length - 30} weitere Einträge</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* NACHRICHTEN TAB */}
        <TabsContent value="nachrichten">
          {nachrichten.gesamt === 0 ? (
            <div className="text-center py-16">
              <MessageSquare size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <h3 className="font-semibold text-slate-500 dark:text-slate-400">Keine Nachrichten</h3>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Benutzer können Ihnen Verbesserungsvorschläge senden</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="nachrichten-list">
              {nachrichten.nachrichten.map(msg => (
                <div
                  key={msg.id}
                  className={`bg-white dark:bg-slate-800 border rounded-lg p-4 transition-all ${
                    msg.gelesen
                      ? 'border-slate-200 dark:border-slate-700'
                      : 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10'
                  }`}
                  data-testid={`nachricht-${msg.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`mt-0.5 ${msg.gelesen ? 'text-slate-400' : 'text-amber-500'}`}>
                        {msg.gelesen ? <MailOpen size={18} /> : <Mail size={18} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{msg.betreff}</span>
                          {!msg.gelesen && <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full uppercase font-bold">Neu</span>}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase font-medium ${
                            msg.kategorie === 'bug' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                              : msg.kategorie === 'frage' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                              : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                          }`}>{msg.kategorie}</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{msg.nachricht}</p>
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-2">
                          <Clock size={12} /> {new Date(msg.erstellt_am).toLocaleString('de-DE')}
                          <span>·</span> von <span className="font-medium">{msg.user_name || msg.username}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!msg.gelesen && (
                        <Button variant="ghost" size="sm" onClick={() => markGelesen(msg.id)} className="text-slate-400 hover:text-emerald-600" title="Als gelesen markieren">
                          <Check size={16} />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => deleteNachricht(msg.id)} className="text-slate-400 hover:text-red-600" title="Löschen">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PASSWORD RESET REQUESTS TAB */}
        <TabsContent value="reset-anfragen">
          {resetRequests.count === 0 ? (
            <div className="text-center py-16">
              <KeyRound size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <h3 className="font-semibold text-slate-500 dark:text-slate-400">Keine Passwort-Anfragen</h3>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Benutzer können über "Passwort vergessen?" eine Anfrage stellen</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="reset-requests-list">
              {resetRequests.requests.map(req => (
                <div
                  key={req.id}
                  className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/10 rounded-lg p-4"
                  data-testid={`reset-request-${req.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">
                        <AlertCircle size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm flex items-center gap-2">
                          {req.user_name || req.username}
                          <span className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full uppercase font-bold">Ausstehend</span>
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">@{req.username}</p>
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-2">
                          <Clock size={12} /> {new Date(req.created_at).toLocaleString('de-DE')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => approveResetRequest(req.id)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                        data-testid={`approve-reset-${req.id}`}
                      >
                        <Link2 size={14} /> Link generieren
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteResetRequest(req.id)}
                        className="text-slate-400 hover:text-red-600"
                        title="Ablehnen"
                        data-testid={`delete-reset-${req.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Show approved reset link */}
          {approvedResetLink && (
            <div className="mt-6 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4" data-testid="approved-reset-link">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                  <Check size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-emerald-800 dark:text-emerald-300">Reset-Link für "{approvedResetLink.username}" generiert!</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">Gültig bis: {new Date(approvedResetLink.expiresAt).toLocaleString('de-DE')}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="flex-1 text-xs bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-700 rounded px-3 py-2 font-mono break-all">
                      {approvedResetLink.link}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(approvedResetLink.link);
                        toast.success("Link kopiert!");
                      }}
                      className="shrink-0"
                      data-testid="copy-reset-link"
                    >
                      <Copy size={14} />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Teilen Sie diesen Link mit dem Benutzer (z.B. per E-Mail oder Nachricht).
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setApprovedResetLink(null)}
                  className="text-slate-400 shrink-0"
                >
                  <X size={16} />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* AUDIT-TRAIL TAB */}
        <TabsContent value="audit">
          <AuditTrail />
        </TabsContent>
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus size={20} /> Neuen Beta-Zugang erstellen</DialogTitle>
            <DialogDescription>Login-Daten für einen neuen Beta-Tester anlegen</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Benutzername *</Label>
              <Input value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} placeholder="z.B. tester1" data-testid="new-user-username" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Passwort *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input type={showNewPassword ? "text" : "password"} value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Mind. 6 Zeichen" data-testid="new-user-password" />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setNewUser({...newUser, password: generatePassword()})} title="Generieren"><RefreshCw size={16} /></Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Name</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} placeholder="Anzeigename (optional)" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">E-Mail</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="E-Mail (optional)" />
            </div>
            {newUser.username && newUser.password && (
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">Zugangsdaten zum Teilen:</p>
                <div className="flex items-center justify-between bg-white dark:bg-slate-800 rounded px-3 py-2 border border-slate-200 dark:border-slate-700">
                  <code className="text-xs font-mono">Benutzer: {newUser.username} | Passwort: {newUser.password}</code>
                  <button onClick={() => copyToClipboard(`Benutzer: ${newUser.username}\nPasswort: ${newUser.password}`, 'credentials')} className="ml-2 text-slate-400 hover:text-blue-500">
                    {copiedField === 'credentials' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowCreateDialog(false)}>Abbrechen</Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreateUser} data-testid="confirm-create-user"><Plus size={16} className="mr-2" />Erstellen</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={20} /> Passwort zurücksetzen</DialogTitle>
            <DialogDescription>Neues Passwort für "{resetTarget?.username}" setzen</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Neues Passwort</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input type={showResetPassword ? "text" : "password"} value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Mind. 6 Zeichen" />
                  <button type="button" onClick={() => setShowResetPassword(!showResetPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showResetPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setResetPassword(generatePassword())}><RefreshCw size={16} /></Button>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => { setShowResetDialog(false); setResetPassword(""); }}>Abbrechen</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={handleResetPassword}>Zurücksetzen</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
